// Client for GET|POST /api/sales/daily-checklist — same auth as Ads Assistant (Bearer + optional workspace org header).
import { supabase } from "@/lib/supabase";
import { readWorkspaceOrgIdFromStorage } from "@/lib/workspaceOrgStorage";

const BASE = "/api/sales/daily-checklist";

export type SalesDailyChecklistItemKind = "computed" | "manual";

export type OppTodoChecklistUrgency = "past_due" | "approaching" | "undated";

export type SalesDailyChecklistItem = {
  id: string;
  label: string;
  description?: string;
  kind: SalesDailyChecklistItemKind;
  done: boolean;
  /** Second-step confirm in UI (e.g. month-end inventory) before marking done. */
  requires_confirm?: boolean;
  /** Present on `ad_logged`: posts counted toward the daily cap. */
  ad_progress?: {
    done: number;
    target: number;
    by_platform?: { key: string; label: string; done: number; target: number }[];
  };
  /** OIP-4 — open opportunity todo rows (`opp_todo:{itemId}`). */
  opp_todo_urgency?: OppTodoChecklistUrgency;
  opp_todo_opportunity_id?: string;
  opp_todo_due_date?: string | null;
};

export type SalesDailyChecklistMeta = {
  ads_mode: "shared" | "per_platform";
  unsupported_pull_platform_keys: string[];
  /** Org-configured stale-lead threshold (days); null when unset. */
  lead_followup_hygiene_days?: number | null;
  /** Escalation cadence (e.g. 3, 7, 14) when hygiene is enabled. */
  lead_followup_hygiene_cadence_days?: number[] | null;
  /** Latest manager/trainer broadcast (Location Tools); same text as header notifications for this nudge. */
  org_manager_nudge?: { title: string; body: string; sent_at: string } | null;
  ack_blocking_enabled?: boolean;
  pending_required_ack_count?: number;
};

type FetchResult<T> = { ok: true; status: number; data: T } | { ok: false; status: number; error: string };

async function salesChecklistFetch<T>(path: string, init: RequestInit = {}): Promise<FetchResult<T>> {
  if (!supabase) return { ok: false, status: 503, error: "App is not configured for sign-in." };

  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) return { ok: false, status: 401, error: "Sign in to use your daily checklist." };

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    ...(init.headers as Record<string, string> | undefined),
  };
  if (init.body != null && !headers["Content-Type"] && !headers["content-type"]) {
    headers["Content-Type"] = "application/json";
  }
  const ws = readWorkspaceOrgIdFromStorage();
  if (ws) (headers as Record<string, string>)["X-Workspace-Organization-Id"] = ws;

  const r = await fetch(path, { ...init, headers });
  const text = await r.text();
  let json: unknown = null;
  try {
    json = JSON.parse(text) as T;
  } catch {
    /* non-JSON */
  }

  if (!r.ok) {
    const err =
      json && typeof json === "object" && json !== null && "error" in json && typeof (json as { error: unknown }).error === "string"
        ? (json as { error: string }).error
        : text || `HTTP ${r.status}`;
    return { ok: false, status: r.status, error: err };
  }

  return { ok: true, status: r.status, data: json as T };
}

/** Local calendar YYYY-MM-DD in the browser timezone. */
export function salesChecklistLocalCalendarDay(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Midnight-to-end-of-day ISO bounds for ad `published_at` (same local calendar day). */
export function salesChecklistLocalDayBounds(): { published_after: string; published_before: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  return { published_after: start.toISOString(), published_before: end.toISOString() };
}

export async function fetchSalesDailyChecklist(params: {
  day: string;
  published_after: string;
  published_before: string;
}): Promise<
  FetchResult<{
    ok: true;
    day: string;
    window: { published_after: string; published_before: string };
    fraction: { done: number; total: number };
    all_done: boolean;
    items: SalesDailyChecklistItem[];
    checklist_meta?: SalesDailyChecklistMeta;
  }>
> {
  const sp = new URLSearchParams();
  sp.set("day", params.day);
  sp.set("published_after", params.published_after);
  sp.set("published_before", params.published_before);
  return salesChecklistFetch(`${BASE}?${sp}`, { method: "GET" });
}

export async function toggleSalesDailyChecklistItem(body: {
  day: string;
  item_key: string;
  done: boolean;
}): Promise<FetchResult<{ ok: true; day: string; item_key: string; done: boolean }>> {
  return salesChecklistFetch(BASE, { method: "POST", body: JSON.stringify(body) });
}
